import React, {
  useMemo,
  useState
} from "react";

import {
  Alert,
  Button,
  Flex,
  FlexItem
} from "@patternfly/react-core";

import EducationDirectionPageSkeleton
  from "./EducationDirectionPageSkeleton";

  import EducationDirectionHeroSkeleton
  from "./EducationDirectionHeroSkeleton";

import {
  ArrowRightIcon
} from "@patternfly/react-icons";

import {
  Link
} from "react-router-dom";

import Carousel from "shared/components/Carousel/Carousel";

import EducationProfessionSelect from "./EducationProfessionSelect";

import EducationDirectionHero
  from "./EducationDirectionHero";

import useEducationDirections from "../hooks/useEducationDirections";

const RANDOMIZED_DIRECTION_IDS = new Set([
  "it-technician",
  "programmer-technician",
  "nutrition-technician",
  "multi-profession"
]);


const shuffleItems = (items) => {
  const shuffledItems = [...items];

  for (
    let index = shuffledItems.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex =
      Math.floor(Math.random() * (index + 1));

    [
      shuffledItems[index],
      shuffledItems[randomIndex]
    ] = [
      shuffledItems[randomIndex],
      shuffledItems[index]
    ];
  }

  return shuffledItems;
};

const selectRandomImage = (
  direction,
  {
    useCarouselImage = false
  } = {}
) => {
  if (
    useCarouselImage &&
    direction.carousel?.image
  ) {
    return direction.carousel.image;
  }

  const images =
    direction.hero?.images ?? [];

  if (images.length > 0) {
    return images[
      Math.floor(Math.random() * images.length)
    ];
  }

  if (direction.hero?.image) {
    return {
      src: direction.hero.image,
      alt: direction.hero.imageAlt ?? ""
    };
  }

  return null;
};


const EducationDirectionsCarousel = () => {
  const {
    educationDirections,
    isLoading,
    error
  } = useEducationDirections();

  const [
    professionSelections,
    setProfessionSelections
  ] = useState({});

  const [
    carouselImages,
    setCarouselImages
  ] = useState({});

  const directions = useMemo(() => {
    const allDirections =
      Object.values(
        educationDirections ?? {}
      );

    const randomizedDirections = shuffleItems(
        allDirections.filter((direction) =>
        RANDOMIZED_DIRECTION_IDS.has(direction.id)
        )
    );

    const remainingDirections =
        allDirections.filter((direction) =>
        !RANDOMIZED_DIRECTION_IDS.has(direction.id)
        );

    return [
        ...randomizedDirections,
        ...remainingDirections
    ].map((direction) => ({
        ...direction,
        selectedImage:
          selectRandomImage(
            direction,
            {
              useCarouselImage: true
            }
          )
    }));
  }, [educationDirections]);

  const handleProfessionChange = (
    direction,
    professionId
  ) => {
    const profession =
      direction.professions?.find(
        item => item.id === professionId
      );

    /*
    * Wyczyszczenie wyboru zawodu:
    * wracamy do głównej grafiki karuzeli.
    */
    if (!profession) {
      setProfessionSelections(previous => {
        const next = {
          ...previous
        };

        delete next[direction.id];

        return next;
      });

      setCarouselImages(previous => ({
        ...previous,
        [direction.id]: selectRandomImage(
          direction,
          {
            useCarouselImage: true
          }
        )
      }));

      return;
    }

    setProfessionSelections(previous => ({
      ...previous,
      [direction.id]: {
        professionId
      }
    }));

    setCarouselImages(previous => ({
      ...previous,
      [direction.id]:
        selectRandomImage(profession)
    }));
  };
    const handleSlideChange = (
      _activeIndex,
      direction
    ) => {
      const selection =
        professionSelections[direction.id];

      const selectedProfession =
        direction.professions?.find(
          profession =>
            profession.id ===
            selection?.professionId
        ) ?? null;

      const image =
        selectedProfession
          ? selectRandomImage(
              selectedProfession
            )
          : selectRandomImage(
              direction,
              {
                useCarouselImage: true
              }
            );

      setCarouselImages(previous => ({
        ...previous,
        [direction.id]: image
      }));
    };
  
  if (isLoading) {
    return (
      <EducationDirectionHeroSkeleton />
    );
  }

  if (error) {
    return (
      <Alert
        variant="danger"
        title="Nie udało się pobrać oferty kształcenia"
      >
        {error.message}
      </Alert>
    );
  }

  if (!directions.length) {
    return null;
  }
  
  return (
    <Carousel
      items={directions}
      getKey={(direction) => direction.id}
      ariaLabel="Przegląd oferty kształcenia"
      showCounter
      controlsAlignment="center"
      autoPlayInterval={8000}
      pauseOnHover
      pauseOnFocus
      onSlideChange={handleSlideChange}
      renderItem={(
        direction,
        activeIndex,
        controls
      ) => {
        const selection =
          professionSelections[direction.id];

        const selectedProfessionId =
          selection?.professionId ?? null;

        const hasProfessions =
          Boolean(direction.professions?.length);

        const shouldShowDirectionButton =
          !hasProfessions ||
          Boolean(selectedProfessionId);

        const selectedProfession =
          direction.professions?.find(
            profession =>
              profession.id ===
              selectedProfessionId
          ) ?? null;

        const displayedDirection =
          selectedProfession
            ? {
                ...direction,
                professionNumber:
                  selectedProfession.professionNumber
              }
            : direction;

        const displayedImage =
          carouselImages[direction.id] ??
          direction.selectedImage;

        const directionPath =
          `/direction/${
            direction.symbol.toLowerCase()
          }`;

        const directionUrl =
          selectedProfessionId
            ? `${directionPath}?profession=${encodeURIComponent(
                selectedProfessionId
              )}`
            : directionPath;

        return (
          <EducationDirectionHero
            direction={displayedDirection}
            image={displayedImage}
            titleComponent="h2"
            isCarouselSlide
            professionSelector={
              direction.professions?.length ? (
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <EducationProfessionSelect
                    professions={
                      direction.professions
                    }
                    selectedProfessionId={
                      selectedProfessionId
                    }
                    onProfessionChange={(
                      professionId
                    ) => {
                      handleProfessionChange(
                        direction,
                        professionId
                      );
                    }}
                  />
                </div>
              ) : null
            }
            footer={
              <Flex
                alignItems={{
                  default: "alignItemsCenter"
                }}
                justifyContent={{
                  default:
                    "justifyContentSpaceBetween"
                }}
                gap={{
                  default: "gapMd"
                }}
                flexWrap={{
                  default: "wrap"
                }}
              >
                <FlexItem>
                  {controls}
                </FlexItem>

                {/* <FlexItem>
                  <Button
                    component={Link}
                    to={directionUrl}
                    variant="primary"
                    icon={<ArrowRightIcon />}
                    iconPosition="end"
                  >
                    {selectedProfessionId
                      ? "Zobacz zawód"
                      : "Zobacz kierunek"}
                  </Button>
                </FlexItem> */}
                {shouldShowDirectionButton && (
                  <FlexItem>
                    <Button
                      component={Link}
                      to={directionUrl}
                      variant="primary"
                      icon={<ArrowRightIcon />}
                      iconPosition="end"
                    >
                      {hasProfessions
                        ? "Zobacz zawód"
                        : "Zobacz kierunek"}
                    </Button>
                  </FlexItem>
                )}
              </Flex>
            }
          />
        );
      }}
      />
  );
};


export default EducationDirectionsCarousel;